import { Logger, Presenter } from 'wdc-cube'
import * as lang from 'src/utils/lang'
import { type TCMTexts } from './tcm_texts'
import FilterManager from './tcm_filter_manager'
import { TheConsumerMarketPresenter } from './tcm_presenter'
import { TableAtividadeEconomicaScope, type TableAtividadeEconomicaRow } from './tcm_scopes'
import { TheConsumerMarketService, type EmpresaFilter, JunctionMode, StringOperator } from './tcm_service'

const LOG = Logger.get('TcmPresenterForEmpresasTableAtivEconomica')

const service = TheConsumerMarketService.singleton()

export default class TcmPresenterForEmpresasTableAtivEconomica extends Presenter<
    TableAtividadeEconomicaScope,
    TheConsumerMarketPresenter
> {
    // :: Class methods

    constructor(owner: TheConsumerMarketPresenter) {
        super(owner, owner.scope.atividadeEconomica)
        this.__parent = owner
        this.__texts = owner.texts
        this.__filterManager = owner.filterManager
    }

    // :: Instance

    private readonly __parent: TheConsumerMarketPresenter
    private readonly __texts: TCMTexts
    private readonly __filterManager: FilterManager

    // :: Public API

    async initialize() {
        this.scope.update = this.update
        this.scope.caption = this.__texts.atividadeEconomica
        this.scope.onItemClicked = this.action(this.__onAtivEconomicaClicked)
        LOG.debug('ready')
    }

    override release(): void {
        super.release()
    }

    clear() {
        this.__filterManager.atividadeEconomicaCodes.clear()
        this.__filterManager.atividadeEconomicaTexts.clear()
    }

    async fetch(filter: EmpresaFilter) {
        this.scope.loaded = false
        try {
            const resp = await service.fetch({
                ativ_economica_com_mais_empresas_funcionarios: true,
                filter: filter
            })

            const dataset: TableAtividadeEconomicaRow[] = []
            for (const bucket of resp.ativ_economica_com_mais_empresas_funcionarios?.buckets ?? []) {
                dataset.push({
                    id: bucket.key,
                    descricao: bucket.description.subclasse,
                    qtdEmpresas: bucket.total_empresas.value,
                    qtdFuncionarios: bucket.total_empregados.value
                })
            }

            dataset.sort((a, b) => {
                let v = b.qtdEmpresas - a.qtdEmpresas
                if (v === 0) {
                    v = b.qtdFuncionarios - a.qtdFuncionarios
                }
                return v
            })

            this.scope.dataset = dataset

            this.__markSelected()
        } finally {
            this.scope.loaded = true
        }
    }

    private __markSelected() {
        const codigoValMap = this.collectExplicitValues()

        for (const row of this.scope.dataset) {
            row.selected = codigoValMap.has(row.id)
        }
        this.scope.update()
    }

    private async __onAtivEconomicaClicked(cnae: string) {
        const codigoValMap = this.collectExplicitValues()

        if (codigoValMap.has(cnae)) {
            codigoValMap.delete(cnae)
        } else {
            codigoValMap.set(cnae, true)
        }

        const prop = this.__filterManager.tipoAtividadeEconomicaTexts
        prop.clear()
        if (codigoValMap.size > 0) {
            prop.add({
                mode: JunctionMode.AND,
                operator: StringOperator.contains,
                value: [...codigoValMap.keys()].join(','),
                domain: 1
            })
        }

        this.owner.filterPane.tipoCnaeCodes.populateUsingValueProporty(1)

        this.__markSelected()
        this.__parent.fetchData(prop.size ? 'totalPorAtividadeEconomica' : '')
    }

    private collectExplicitValues() {
        const prop = this.__filterManager.tipoAtividadeEconomicaTexts

        const codigoValMap = new Map<string, boolean>()
        for (const filterVal of prop.values()) {
            if (filterVal.value) {
                if (filterVal.operator == StringOperator.contains) {
                    stractCodes(filterVal.value).forEach((codigo) => codigo && codigoValMap.set(codigo, true))
                    continue
                }

                if (filterVal.operator == StringOperator.does_not_contain) {
                    stractCodes(filterVal.value).forEach((codigo) => codigoValMap.delete(codigo))
                    continue
                }

                if (filterVal.operator == StringOperator.is_in) {
                    stractCodes(filterVal.value).forEach((codigo) => codigo && codigoValMap.set(codigo, true))
                    continue
                }

                if (filterVal.operator == StringOperator.is_not_in) {
                    stractCodes(filterVal.value).forEach((codigo) => codigoValMap.delete(codigo))
                    continue
                }

                if (filterVal.operator == StringOperator.is) {
                    stractCodes(filterVal.value).forEach((codigo) => codigoValMap.set(codigo, true))
                    continue
                }

                if (filterVal.operator == StringOperator.is_not) {
                    stractCodes(filterVal.value).forEach((codigo) => codigoValMap.delete(codigo))
                    continue
                }
            }
        }
        return codigoValMap
    }
}

function stractCodes(val: string | undefined): string[] {
    if (!val) {
        return []
    }

    const parts = lang.splitValues(val)
    const codes = new Set<string>()

    for (const part of parts) {
        const num = lang.onlyNumbers(part)
        num && codes.add(num)
    }

    return [...codes]
}
